import { isValueSetReadError } from "#common/graphql/Attribute";
import { isDuplicateSpaceNameError } from "../SpaceEditor/errors";
import type { VaultDocument, Space, VaultDomain } from "#common/types/vault";

/** A domain paired with the spaces that belong to it, for the grouped space filter. */
export type DomainGroup = { domain: VaultDomain; spaces: Space[] };

/**
 * Group spaces under their domain, in `order` (falling back to the order of
 * `domains`). Domains with no visible spaces are dropped.
 */
export function groupSpacesByDomain(
  spaces: Space[],
  domains: VaultDomain[],
  order?: string[],
): DomainGroup[] {
  const domainById = new Map(domains.map((d) => [d.id, d] as const));
  const ids = order && order.length ? order : domains.map((d) => d.id);
  return ids
    .map((id) => {
      const domain = domainById.get(id);
      if (!domain) return null;
      const list = spaces.filter((s) => s.domain === id);
      return list.length ? { domain, spaces: list } : null;
    })
    .filter((g): g is DomainGroup => g !== null);
}

/** Count documents per space id. */
export function countDocsBySpace(documents: VaultDocument[]): Record<string, number> {
  const counts: Record<string, number> = {};
  documents.forEach((d) => {
    counts[d.space] = (counts[d.space] ?? 0) + 1;
  });
  return counts;
}

/** Wire codes this shell can phrase better than the raw server text. */
const SAVE_ERROR_BY_CODE: Record<string, string> = {
  FORBIDDEN_SYSTEM_BOUND: "this attribute is owned by engineering and can't be edited here",
  FORBIDDEN: "you don't have permission to make this change",
};

/** `Couldn't save "X" — <reason>`, adding the terminal stop `reason` lacks. */
function saveErrorMessage(name: string, reason: string): string {
  const punctuated = /[.!?]$/.test(reason) ? reason : `${reason}.`;
  return `Couldn't save “${name}” — ${punctuated}`;
}

/** The phrasing for whichever mapped wire code `error` carries, or `null`. */
function mappedErrorReason(error: unknown): string | null {
  const raw = error instanceof Error ? error.message : "";
  // Longest match wins: `FORBIDDEN` is a prefix of `FORBIDDEN_SYSTEM_BOUND`, so
  // first-match would make the specific code depend on key order.
  const code = Object.keys(SAVE_ERROR_BY_CODE)
    .filter((c) => raw.includes(c))
    .sort((a, b) => b.length - a.length)[0];
  return code ? SAVE_ERROR_BY_CODE[code] : null;
}

/**
 * Why a global-attribute save was rejected, phrased for the admin. Takes
 * `unknown` so the presentational shell stays free of Apollo types.
 */
export function globalAttrSaveErrorMessage(name: string, error: unknown): string {
  return saveErrorMessage(
    name,
    mappedErrorReason(error) ?? "the change was rejected, so nothing was saved",
  );
}

/**
 * Why a promotion to global was rejected (DV-648). Shares the wire-code
 * phrasing with the save path but leads differently, since nothing was being
 * saved — the attribute simply stayed space-local.
 *
 * `ATTRIBUTE_LABEL_CONFLICT` is deliberately absent from `SAVE_ERROR_BY_CODE`
 * and falls through to the generic reason: DV-511 owns surfacing that one
 * inline, under the rename field where the steward can act on it.
 */
export function globalAttrPromoteErrorMessage(name: string, error: unknown): string {
  const reason = mappedErrorReason(error) ?? "the promotion was rejected";
  const punctuated = /[.!?]$/.test(reason) ? reason : `${reason}.`;
  return `Couldn't promote “${name}” — ${punctuated}`;
}

const OPAQUE_SAVE_ERROR_RE =
  /Response not successful|Failed to fetch|NetworkError|status code \d{3}|Cannot query field|Unknown argument|Expected type/i;

export function spaceSaveErrorMessage(name: string, error: unknown): string {
  const raw = (error instanceof Error ? error.message : "").trim();
  if (isDuplicateSpaceNameError(error)) {
    return saveErrorMessage(
      name,
      "a space with this name already exists. Space names must be unique",
    );
  }
  const reason =
    raw && !OPAQUE_SAVE_ERROR_RE.test(raw)
      ? raw
      : "the change was rejected, so nothing was saved";
  return saveErrorMessage(name, reason);
}

/** What an accepted global-attribute save says, wherever it is reported from. */
export function globalAttrSaveSuccessMessage(name: string): string {
  return `Saved “${name}” — the change applies everywhere it's referenced`;
}

/** What an accepted promotion says (DV-648). */
export function globalAttrPromoteSuccessMessage(name: string): string {
  return `Promoted “${name}” to the global library — it can now be added to any space`;
}

export function spaceLocalDeleteSuccessMessage(name: string): string {
  return `Deleted “${name}” — it no longer appears as a filter, column, or upload field`;
}

export function spaceLocalDeleteErrorMessage(name: string, error: unknown): string {
  const reason = mappedErrorReason(error) ?? "the deletion was rejected";
  const punctuated = /[.!?]$/.test(reason) ? reason : `${reason}.`;
  return `Couldn't delete “${name}” — ${punctuated}`;
}

/**
 * What an accepted space-local attribute save says (DV-674). Leads on scope,
 * which is the whole point of a space-local — where the global counterpart says
 * "everywhere it's referenced". `spaceName` is optional because the edited row
 * may come from a grid page whose space was never in `spaces` to name.
 */
export function spaceLocalAttrSaveSuccessMessage(name: string, spaceName?: string): string {
  return `Saved “${name}” — the change applies to ${spaceName ?? "this space"} only`;
}

/**
 * Why a space-local attribute save was rejected (DV-674). Shares the wire-code
 * phrasing with the global save path.
 */
export function spaceLocalAttrSaveErrorMessage(name: string, error: unknown): string {
  // A failed pre-save read is not a refusal of the edit — the mutation was never
  // attempted — so it must not be reported as one. Retrying is the whole point:
  // the save is abandoned rather than sent against a stale value set.
  if (isValueSetReadError(error)) {
    return saveErrorMessage(name, "its current values couldn’t be checked — try again");
  }
  return saveErrorMessage(
    name,
    mappedErrorReason(error) ?? "the change was rejected, so nothing was saved",
  );
}

const REQUIRED_GUARANTEED_VALUE_RE =
  /^attribute ([\w-]+) is required and (?:cannot be cleared|must have a value)\.?$/i;

/**
 * Phrase a document-detail attribute save failure for the admin. Maps known DVB
 * validation text to the same copy the save bar uses; passes through other
 * server-written messages when they are already human-readable.
 */
export function documentDetailSaveErrorMessage(
  error: unknown,
  labelsBySlug: Record<string, string> = {},
): string {
  const raw = (error instanceof Error ? error.message : String(error)).trim();
  if (!raw) {
    return "Couldn't save changes — the update was rejected.";
  }
  const required = REQUIRED_GUARANTEED_VALUE_RE.exec(raw);
  if (required) {
    const label = labelsBySlug[required[1]] ?? required[1];
    return `Pick a value for ${label} — clearing it can\u2019t be saved yet`;
  }
  if (OPAQUE_SAVE_ERROR_RE.test(raw)) {
    return "Couldn't save changes — the update was rejected.";
  }
  return raw;
}

/** Two-letter initials for an avatar, e.g. "Dana Liu" → "DL". */
export function initials(name: string): string {
  return name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}
